import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { getData, putData } from "../../../FetchApiServices";

const VENDOR_ROLE_NAMES = [
  "Vendor Initiator",
  "Vendor Checker",
  "Finance Data Controller",
  "MDM Approver",
];

const ROLE_ICONS = {
  "Vendor Initiator":         "bi-pencil-square",
  "Vendor Checker":           "bi-check2-circle",
  "Finance Data Controller":  "bi-bank",
  "MDM Approver":             "bi-shield-check",
};

const ROLE_COLORS = {
  "Vendor Initiator":        "#3B82F6",
  "Vendor Checker":          "#8B5CF6",
  "Finance Data Controller": "#F59E0B",
  "MDM Approver":            "#10B981",
};

export default function VendorRolesAdmin() {
  const [users, setUsers]   = useState([]);
  const [roles, setRoles]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(null); // user_id being saved
  const [msg, setMsg]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [uRes, rRes] = await Promise.all([
      getData("users?is_t2t=true"),
      getData("t2t/roles"),
    ]);
    if (!uRes.error) setUsers(uRes.data?.data || []);
    if (!rRes.error) {
      const all = rRes.data?.data || rRes.data?.roles || [];
      setRoles(all.filter((r) => VENDOR_ROLE_NAMES.includes(r.name)));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const changeRole = async (user, newRoleId) => {
    setSaving(user.user_id);
    const res = await putData(`users/${user.user_id}`, {
      name:        user.name,
      email:       user.email,
      status:      user.status,
      t2t_role_id: newRoleId || null,
    });
    setSaving(null);
    if (res.error) { flash(res.error.message || "Failed to update role.", "error"); return; }
    flash(`${user.name} role updated.`);
    setUsers((prev) => prev.map((u) =>
      u.user_id === user.user_id
        ? { ...u, t2t_role_id: newRoleId || null, t2t_role_name: roles.find((r) => r.role_id === Number(newRoleId))?.name || null }
        : u
    ));
  };

  // Group users by vendor role
  const vendorRoles = VENDOR_ROLE_NAMES.map((roleName) => ({
    name:  roleName,
    icon:  ROLE_ICONS[roleName],
    color: ROLE_COLORS[roleName],
    users: users.filter((u) => u.t2t_role_name === roleName),
  }));
  const unassigned = users.filter((u) => !VENDOR_ROLE_NAMES.includes(u.t2t_role_name));

  if (loading) return (
    <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>
      Loading vendor role assignments…
    </div>
  );

  return (
    <div>
      {msg && (
        <div style={{
          padding: "8px 14px", borderRadius: 6, marginBottom: 12, fontSize: 13,
          background: msg.type === "error" ? "#FEF2F2" : "#F0FDF4",
          color: msg.type === "error" ? "#DC2626" : "#16A34A",
          border: `1px solid ${msg.type === "error" ? "#FECACA" : "#BBF7D0"}`,
        }}>
          {msg.text}
        </div>
      )}

      <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 20 }}>
        Assign users to the 4 vendor workflow roles. Each user can hold one T2T role at a time.
        Use the dropdown on each user row to change their role.
      </p>

      {/* 4-role grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, marginBottom: 24 }}>
        {vendorRoles.map((role) => (
          <div key={role.name} style={{
            border: `1px solid ${role.color}33`,
            borderRadius: 10,
            padding: 16,
            background: role.color + "08",
          }}>
            {/* Role header */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: role.color + "22",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className={`bi ${role.icon}`} style={{ color: role.color, fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{role.name}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                  {role.users.length} user{role.users.length !== 1 ? "s" : ""} assigned
                </div>
              </div>
            </div>

            {/* User rows */}
            {role.users.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: "8px 0", textAlign: "center", fontStyle: "italic" }}>
                No users assigned
              </div>
            ) : (
              role.users.map((u) => (
                <UserRow key={u.user_id} user={u} roles={roles} saving={saving} onChangeRole={changeRole} />
              ))
            )}
          </div>
        ))}
      </div>

      {/* Unassigned users */}
      <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: "var(--t2t-text-secondary)" }}>
          <i className="bi bi-person-x" style={{ marginRight: 6 }} />
          Unassigned / Other Roles
          <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 11 }}>({unassigned.length})</span>
        </div>
        {unassigned.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", fontStyle: "italic" }}>All users have vendor roles assigned.</div>
        ) : (
          unassigned.map((u) => (
            <UserRow key={u.user_id} user={u} roles={roles} saving={saving} onChangeRole={changeRole} />
          ))
        )}
      </div>
    </div>
  );
}

UserRow.propTypes = {
  user:         PropTypes.shape({ user_id: PropTypes.number, name: PropTypes.string, email: PropTypes.string, t2t_role_id: PropTypes.number }).isRequired,
  roles:        PropTypes.arrayOf(PropTypes.shape({ role_id: PropTypes.number, name: PropTypes.string })).isRequired,
  saving:       PropTypes.number,
  onChangeRole: PropTypes.func.isRequired,
};

function UserRow({ user, roles, saving, onChangeRole }) {
  const isSaving = saving === user.user_id;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "6px 0",
      borderBottom: "1px solid #F1F5F9",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: "#E2E8F0",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 700, color: "#64748B", flexShrink: 0,
      }}>
        {user.name?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
        <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
      </div>
      <select
        disabled={isSaving}
        value={user.t2t_role_id || ""}
        style={{
          fontSize: 11, padding: "3px 6px", borderRadius: 5,
          border: "1px solid #CBD5E1", background: "#fff",
          cursor: isSaving ? "not-allowed" : "pointer",
          minWidth: 140,
        }}
        onChange={(e) => onChangeRole(user, e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">— No Role —</option>
        {roles.map((r) => (
          <option key={r.role_id} value={r.role_id}>{r.name}</option>
        ))}
      </select>
      {isSaving && <i className="bi bi-arrow-repeat" style={{ fontSize: 12, color: "#94A3B8", animation: "spin 1s linear infinite" }} />}
    </div>
  );
}
